import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { X, Check, Sparkles, ArrowRight, Loader2 } from 'lucide-react'
import Card from './Card'
import Button from './Button'

const UpgradeSubscriptionModal = ({
  currentTier,
  availableTiers,
  selectedTier,
  onSelectTier,
  onUpgrade,
  onClose,
  upgrading,
  loadingTiers,
}) => {
  const [billingCycle, setBillingCycle] = useState('monthly')
  const tierColors = {
    demo: 'from-gray-500 to-gray-600',
    lite: 'from-teal-500 to-teal-600',
    essential: 'from-blue-500 to-blue-600',
    professional: 'from-green-500 to-green-600',
    growth: 'from-purple-500 to-purple-600', // Code is 'growth', display name is 'Enterprise'
    white_label: 'from-indigo-500 to-indigo-600',
    enterprise: 'from-amber-500 to-amber-600',
  }

  const tierLabels = {
    demo: 'Demo',
    lite: 'Lite',
    essential: 'Essential',
    professional: 'Professional',
    growth: 'Enterprise', // Display name changed from 'Growth' to 'Enterprise'
    white_label: 'White Label',
    enterprise: 'Enterprise',
  }

  // Helper to get ZAR pricing from metadata, fallback to USD
  const getPrice = (tier, period = 'monthly') => {
    if (!tier) return { amount: 0, currency: 'USD' }
    const zarPrice = tier.metadata?.zar_price_monthly || tier.metadata?.zar_price_yearly
    if (zarPrice && period === 'monthly') {
      return { amount: tier.metadata?.zar_price_monthly || 0, currency: 'ZAR' }
    }
    if (zarPrice && period === 'yearly') {
      return { amount: tier.metadata?.zar_price_yearly || 0, currency: 'ZAR' }
    }
    return {
      amount: period === 'monthly' ? tier.price_monthly : tier.price_yearly,
      currency: tier.currency || 'USD',
    }
  }

  if (loadingTiers) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl"
          >
            <Card variant="glass" className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading upgrade options...</p>
            </Card>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    )
  }

  if (availableTiers.length === 0) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md"
          >
            <Card variant="glass" className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">No Upgrades Available</h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You're already on the highest available subscription tier. Contact us for enterprise options.
              </p>
              <Button onClick={onClose} className="w-full">
                Close
              </Button>
            </Card>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        >
          <Card variant="glass" className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Upgrade Subscription</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Choose a plan that fits your needs
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Current Plan */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Current Plan:</strong> {tierLabels[currentTier] || currentTier}
              </p>
            </div>

            {/* Billing Cycle Toggle */}
            {selectedTier && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Select Billing Cycle
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      billingCycle === 'monthly'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold">Monthly</div>
                    <div className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                      {(() => {
                        const price = getPrice(selectedTier, 'monthly')
                        const symbol = price.currency === 'ZAR' ? 'R' : '$'
                        return `${symbol}${price.amount.toLocaleString()}/month`
                      })()}
                    </div>
                    <div className="text-xs mt-1 text-primary-600 dark:text-primary-400 font-medium">
                      Recurring subscription
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 transition-colors ${
                      billingCycle === 'yearly'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold">Yearly</div>
                    <div className="text-xs mt-1 text-gray-600 dark:text-gray-400">
                      {(() => {
                        const price = getPrice(selectedTier, 'yearly')
                        const symbol = price.currency === 'ZAR' ? 'R' : '$'
                        return `${symbol}${price.amount.toLocaleString()}/year`
                      })()}
                    </div>
                    {(() => {
                      const monthlyPrice = getPrice(selectedTier, 'monthly')
                      const yearlyPrice = getPrice(selectedTier, 'yearly')
                      if (monthlyPrice.amount > 0 && yearlyPrice.amount > 0) {
                        const savings = Math.round((1 - (yearlyPrice.amount / 12) / monthlyPrice.amount) * 100)
                        return (
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1 font-semibold">
                            Save {savings}%
                          </div>
                        )
                      }
                      return null
                    })()}
                  </button>
                </div>
              </div>
            )}

            {/* Available Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {availableTiers.map((tier) => {
                const isSelected = selectedTier?.id === tier.id
                return (
                  <motion.div
                    key={tier.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Card
                      variant="soft"
                      className={`p-5 cursor-pointer transition-all ${
                        isSelected
                          ? 'ring-2 ring-primary-500 dark:ring-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => onSelectTier(tier)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-bold text-lg text-gray-900 dark:text-white">
                          {tier.name}
                        </h4>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-primary-500 dark:bg-primary-400 flex items-center justify-center">
                            <Check size={14} className="text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {tier.description}
                      </p>
                      <div className="mb-4">
                        {(() => {
                          const monthlyPrice = getPrice(tier, 'monthly')
                          const yearlyPrice = getPrice(tier, 'yearly')
                          const monthlyAmount = monthlyPrice.amount
                          const yearlyAmount = yearlyPrice.amount
                          const currencySymbol = monthlyPrice.currency === 'ZAR' ? 'R' : '$'
                          return (
                            <>
                              <span className="text-3xl font-bold text-gray-900 dark:text-white">
                                {currencySymbol}{monthlyAmount.toLocaleString()}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">/month</span>
                              {yearlyAmount > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                  {currencySymbol}{yearlyAmount.toLocaleString()}/year
                                  {monthlyAmount > 0 && (
                                    <span className="text-green-600 dark:text-green-400 ml-1">
                                      (save {currencySymbol}{((monthlyAmount * 12) - yearlyAmount).toLocaleString()})
                                    </span>
                                  )}
                                </p>
                              )}
                            </>
                          )
                        })()}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {tier.max_users && (
                          <p>Up to {tier.max_users === -1 ? 'Unlimited' : tier.max_users} users</p>
                        )}
                        {tier.max_clients && (
                          <p>Up to {tier.max_clients === -1 ? 'Unlimited' : tier.max_clients} clients</p>
                        )}
                        {tier.max_storage_gb && (
                          <p>{tier.max_storage_gb === -1 ? 'Unlimited' : tier.max_storage_gb} GB storage</p>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                )
              })}
            </div>

            {/* Upgrade Button */}
            <Button
              onClick={() => onUpgrade(billingCycle)}
              disabled={!selectedTier || upgrading}
              className="w-full"
            >
              {upgrading ? (
                <>
                  <Sparkles size={18} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Upgrade to {selectedTier?.name || 'Plan'}
                  <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>

            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
              Secure payment powered by Paystack. Your subscription will activate immediately after payment.
            </p>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default UpgradeSubscriptionModal

